/**
 * ISS-038 — Skill Activity tab.
 *
 * Reuses the existing ``GET /admin/skills/{id}/usage`` endpoint to show
 * which agents, hives and active conversations are currently consuming the
 * skill, plus the total number of bindings.
 */

import {
  Card,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@/components/ui';
import { useAdminSkillUsage } from '@/features/skills';
import { useI18n } from '@/i18n';

interface SkillActivityTabProps {
  skillId: string;
}

export function SkillActivityTab({ skillId }: SkillActivityTabProps) {
  const { t } = useI18n();
  const { data, isLoading, error } = useAdminSkillUsage(skillId);

  if (isLoading) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <Spinner />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-6 text-center text-destructive">
        {t('admin.skills.activity.loadFailed')}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-sm text-muted-foreground">
            {t('admin.skills.activity.activeConversations')}
          </div>
          <div className="text-2xl font-semibold">{data.conversations_active}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">
            {t('admin.skills.activity.totalBindings')}
          </div>
          <div className="text-2xl font-semibold">{data.total_bindings}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">
            {t('admin.skills.activity.agentsUsingSkill')}
          </div>
          <div className="text-2xl font-semibold">{data.agents.length}</div>
        </div>
      </Card>

      <Card className="p-6 space-y-2">
        <h4 className="text-sm font-semibold">
          {t('admin.skills.activity.agents')}
        </h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.skills.activity.agentId')}</TableHead>
              <TableHead>{t('admin.skills.activity.name')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.agents.length === 0 ? (
              <TableEmpty
                colSpan={2}
                message={t('admin.skills.activity.noAgents')}
              />
            ) : (
              data.agents.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-xs">{a.id}</TableCell>
                  <TableCell>{a.name ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-6 space-y-2">
        <h4 className="text-sm font-semibold">
          {t('admin.skills.activity.hives')}
        </h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.skills.activity.hiveId')}</TableHead>
              <TableHead>{t('admin.skills.activity.name')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.hives.length === 0 ? (
              <TableEmpty
                colSpan={2}
                message={t('admin.skills.activity.noHives')}
              />
            ) : (
              data.hives.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-mono text-xs">{h.id}</TableCell>
                  <TableCell>{h.name ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default SkillActivityTab;
